import { clsx } from 'clsx'
import { Space } from 'component/Space'
import { Spinner } from 'component/Spinner'
import { Typo } from 'component/Typo'
import { useEffectOnce } from 'hook/useEffectOnce'
import { useLatestCallback } from 'hook/useLatestCallback'
import { i18n } from 'i18n'
import { Icon } from 'icon/Icon'
import { observer } from 'mobx-react-lite'
import css from 'page/ProfileScreen/Selfies/CameraInput.module.scss'
import { Photo } from 'page/ProfileScreen/Selfies/Photo'
import { useEffect, useState } from 'react'
import { opt } from 'store/opt'
import { SelfieStore } from 'store/profile/SelfieStore'
import { store } from 'store/store'
import CheckSvg from './asset/check.svg'

type Props = {
  selfie?: SelfieStore
  simple?: boolean
  square?: boolean
}

export const CameraInput = observer(
  (({ selfie, simple, square }: Props) => {
    const { selfies, camera, num } = store.profile.common
    selfie ??= selfies[num]
    const picture = selfie.url
    const error = camera.error || selfie.error
    const multi = camera.hasMultipleCameras
    const [isMobile, setIsMobile] = useState(false)
    const icon = simple ? 48 : opt.stand ? 64 : 24
    const className = clsx(
      css.button,
      opt.stand && css.stand,
      opt.basic && css.basic,
      simple && css.simple,
      square && css.square,
      error && css.error,
    )

    useEffect(() => {
      setIsMobile(true)
    }, [])

    useEffectOnce(() => {
      if (simple) return
      if (opt.camera) selfies[1].clear()
      else selfies[num].clear()
    })

    useEffectOnce(() => {
      return () => camera.stop()
    })

    const onVideo = useLatestCallback((element: HTMLVideoElement) => {
      camera.element = element
      void camera.start()
    })

    const handleSwitchCamera = useLatestCallback(async () => {
      await camera.switchCamera()
    })

    return (
      <Space className={className} center data-camera-input>
        <Space className={clsx(css.circle, picture && css.selected)} oh>
          <Space className={css.inner} center>
            {!selfie.src && (
              <>
                <video
                  key={camera.key}
                  ref={onVideo}
                  className={clsx(
                    css.video,
                    css[`turn${opt.turn}`],
                    opt.mirror && camera.isFrontCamera && css.mirror,
                  )}
                />
              </>
            )}
            {selfie.src && <Photo selfie={selfie} top={square} />}
            {(camera.starting || camera.processing) && (
              <div className={css.loading}>
                <Spinner />
              </div>
            )}
          </Space>
          {isMobile &&
            multi &&
            !simple &&
            !picture &&
            !error &&
            !opt.stand &&
            !camera.starting &&
            !camera.processing && (
              <button
                className={css.rotateButton}
                onClick={handleSwitchCamera}
                type="button"
                title={
                  camera.isFrontCamera
                    ? i18n('SwitchToTheRearCamera')
                    : i18n('SwitchToTheFrontCamera')
                }
              >
                <Icon name="flip_camera_ios" className={css.rotateIcon} />
                <span className={css.rotateText}>
                  {camera.isFrontCamera
                    ? i18n('RearCamera')
                    : i18n('FrontCamera')}
                </span>
              </button>
            )}
        </Space>
        {picture && !opt.stand2 && (
          <CheckSvg className={css.check} />
        )}
        {!!camera.seconds && (
          <Space className={css.countdown} center>
            <Typo size={opt.stand ? 256 : 96} bold one>
              {camera.seconds.toFixed()}
            </Typo>
          </Space>
        )}
      </Space>
    )
  }),
)
